import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  RowSelectionState,
  ColumnFiltersState,
  getFacetedRowModel,
  getFacetedUniqueValues
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  Divider
} from '@mui/material';
import { useCustomBenefitAssignmentColumns } from './columns';
import { useEffect, useMemo, useState } from 'react';
import Pagination from '../shared/pagination';
import { DataTableLoadingBar } from '../shared/loading-bar';
import EmptyTable from '../../../icons/svg/empty-table.svg';
import { useTranslation } from 'react-i18next';
import { SortableTableHeader } from '../shared/sortable-header';
import { CustomBenefitToolBar } from './toolbar';
import { Loading } from '../../atomic';
import { DEFAULT_PAGE_SIZE, scrollableTableContainerSx } from '../shared/utils';
import { MonthFieldRequest } from '@pxtra-client-lib-legacy/gen';
import {
  useListCustomBenefitAssignments,
  useCreateCustomBenefitAssignments,
  useDeleteCustomBenefitAssignments
} from '../../../../queries/benefit/customBenefitAssignment';
import { CustomBenefitTableProvider, CustomBenefitTableContextValue } from './context';
import { globalFilterFn } from '../../../util/filter';

interface CustomBenefitAssignmentTableProps {
  benefitId: string;
  benefitName: string;
  companyAmount: number;
  assignmentsStart: MonthFieldRequest;
  onBulkAssign: (employeeIds: string[]) => void;
}

export const CustomBenefitAssignmentTable = ({
  benefitId,
  benefitName,
  companyAmount,
  assignmentsStart,
  onBulkAssign
}: CustomBenefitAssignmentTableProps) => {
  const { t } = useTranslation();

  const {
    data: assignmentsData = { pages: [], pageParams: [] },
    hasNextPage,
    fetchNextPage,
    isFetching,
    isLoading
  } = useListCustomBenefitAssignments(benefitId, assignmentsStart);

  const flattenedData = useMemo(
    () => assignmentsData.pages.flatMap((page) => page.results),
    [assignmentsData]
  );

  useEffect(() => {
    if (hasNextPage && !isFetching) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetching, fetchNextPage]);

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: DEFAULT_PAGE_SIZE
  });
  const [globalFilter, setGlobalFilter] = useState('');
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [focusedEmployeeId, setFocusedEmployeeId] = useState<string | undefined>();

  const columns = useCustomBenefitAssignmentColumns(benefitName || t('custom:custom_benefit'));

  const { mutate: createAssignments } = useCreateCustomBenefitAssignments(benefitId);
  const { mutate: deleteAssignments } = useDeleteCustomBenefitAssignments(benefitId);

  const table = useReactTable({
    data: flattenedData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    globalFilterFn: globalFilterFn,
    onGlobalFilterChange: setGlobalFilter,
    onRowSelectionChange: setRowSelection,
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getRowId: (row) => row.id ?? 'MISSING_ROW_ID',
    onColumnFiltersChange: setColumnFilters,
    sortDescFirst: false,
    state: {
      pagination,
      globalFilter,
      rowSelection,
      columnFilters
    },
    initialState: {
      columnVisibility: {
        location: false,
        department: false,
        cost_center: false,
        status: false,
        active_benefit: false,
        budget_exhausted: false,
        entry_date: false
      }
    }
  });

  const contextValue: CustomBenefitTableContextValue = useMemo(
    () => ({
      benefitId,
      companyAmount,
      assignmentsStart,
      createAssignments,
      deleteAssignments,
      focusedEmployeeId,
      setFocusedEmployeeId
    }),
    [
      benefitId,
      companyAmount,
      assignmentsStart,
      createAssignments,
      deleteAssignments,
      focusedEmployeeId
    ]
  );

  if (isLoading) {
    return (
      <Paper
        sx={{
          height: 400,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Loading />
      </Paper>
    );
  }

  return (
    <CustomBenefitTableProvider value={contextValue}>
      <Paper className="overflow-y-hidden overflow-x-auto flex flex-col h-full">
        <CustomBenefitToolBar
          table={table}
          benefitName={benefitName}
          onBulkAssign={(employeeIds) => {
            onBulkAssign(employeeIds);
          }}
        />

        <TableContainer sx={scrollableTableContainerSx()}>
          <Table size="small">
            <TableHead>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <SortableTableHeader
                      key={header.id}
                      header={header}
                      isLoading={isFetching || isLoading}
                      hasData={flattenedData.length > 0}
                    />
                  ))}
                </TableRow>
              ))}
              <TableRow>
                <TableCell
                  sx={{
                    padding: 0,
                    borderBottom: 'none',
                    backgroundColor: 'transparent'
                  }}
                  colSpan={columns.length}
                >
                  <DataTableLoadingBar isLoading={isFetching || isLoading} />
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!isFetching && !isLoading && table.getRowModel().rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length} className="!border-none">
                    <div className="flex flex-col items-center justify-center">
                      <img
                        src={EmptyTable}
                        alt={t('export:EMPTY_EMPLOYEE_SELECTION_TABLE')}
                        className="w-64 h-64"
                      />
                      <p className="mt-4 text-gray-500">
                        {t('export:EMPTY_EMPLOYEE_SELECTION_TABLE')}
                      </p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                table.getRowModel().rows.map((row) => (
                  <TableRow key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell className="py-2 border-grey-100" key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Divider className="border-grey-100" />
        <Pagination table={table} />
      </Paper>
    </CustomBenefitTableProvider>
  );
};
